import { useEffect, useState } from 'react';

export default function SuppliersTab({ suppliers, onSaveSupplier, onDeleteSupplier }) {
    const [selectedId, setSelectedId] = useState(null);
    const [purchasedItems, setPurchasedItems] = useState([]);
    const [loadingItems, setLoadingItems] = useState(false);
    const [form, setForm] = useState({
        companyName: '',
        firstName: '',
        lastName: '',
        position: '',
        email: '',
        phone: '',
        otherContact: '',
        contactPerson: '',
        address: '',
        city: '',
        nif: '',
        stat: '',
        comment: '',
    });

    useEffect(() => {
        if (selectedId === null) {
            setForm({
                companyName: '',
                firstName: '',
                lastName: '',
                position: '',
                email: '',
                phone: '',
                otherContact: '',
                contactPerson: '',
                address: '',
                city: '',
                nif: '',
                stat: '',
                comment: '',
            });
            setPurchasedItems([]);
            return;
        }

        const supplier = suppliers.find((item) => item.id === selectedId);
        if (supplier) {
            setForm({
                companyName: supplier.companyName || supplier.company_name || '',
                firstName: supplier.firstName || supplier.first_name || '',
                lastName: supplier.lastName || supplier.last_name || '',
                position: supplier.position || '',
                email: supplier.email || '',
                phone: supplier.phone || '',
                otherContact: supplier.otherContact || supplier.other_contact || '',
                contactPerson: supplier.contactPerson || supplier.contact_person || '',
                address: supplier.address || '',
                city: supplier.city || '',
                nif: supplier.nif || '',
                stat: supplier.stat || '',
                comment: supplier.comment || '',
            });
            
            // Charger les articles achetés chez ce fournisseur
            fetchPurchasedItems(supplier.id);
        }
    }, [selectedId, suppliers]);

    const fetchPurchasedItems = async (supplierId) => {
        setLoadingItems(true);
        try {
            const response = await fetch(`/api/suppliers/${supplierId}/purchased-items`);
            if (response.ok) {
                const items = await response.json();
                setPurchasedItems(items || []);
            } else {
                setPurchasedItems([]);
            }
        } catch (error) {
            console.error('Erreur lors du chargement des articles:', error);
            setPurchasedItems([]);
        } finally {
            setLoadingItems(false);
        }
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        const companyName = form.companyName.trim();
        if (!companyName) return;

        onSaveSupplier({ id: selectedId, ...form });
        setSelectedId(null);
        setForm({
            companyName: '',
            firstName: '',
            lastName: '',
            position: '',
            email: '',
            phone: '',
            otherContact: '',
            contactPerson: '',
            address: '',
            city: '',
            nif: '',
            stat: '',
            comment: '',
        });
    };

    return (
        <div className="space-y-6">
            <section className="rounded-3xl border border-green-400 bg-green-50/90 p-5 shadow-xl shadow-slate-950/20">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                        <p className="text-sm uppercase tracking-[0.35em] text-green-400">Fournisseurs</p>
                        <h2 className="text-3xl font-semibold text-green-900">Gestion des fournisseurs</h2>
                    </div>
                    <p className="max-w-xl text-sm text-green-600">
                        Centralisez les fournisseurs, ajoutez-en de nouveaux et réutilisez-les dans vos commandes et ingrédients.
                    </p>
                </div>
            </section>

            <div className="space-y-6">
                {/* Formulaire en premier */}
                <div className="rounded-3xl border border-green-400 bg-white p-5 shadow-xl shadow-slate-950/20">
                    <h3 className="mb-4 text-xl font-semibold text-green-900">
                        {selectedId ? 'Modifier un fournisseur' : 'Ajouter un fournisseur'}
                    </h3>
                    <form className="space-y-4" onSubmit={handleSubmit}>
                        {/* Row 1: Company & Name */}
                        <div className="grid gap-4 sm:grid-cols-2">
                            <div>
                                <label className="text-sm text-green-600">Nom du fournisseur</label>
                                <input
                                    value={form.companyName}
                                    onChange={(event) => setForm({ ...form, companyName: event.target.value })}
                                    className="mt-2 w-full rounded-3xl border border-green-400 bg-green-50 px-4 py-3 text-green-900 focus:border-green-500 focus:outline-none"
                                    placeholder="Ex. Fournisseur X"
                                />
                            </div>
                            <div>
                                <label className="text-sm text-green-600">Prénom</label>
                                <input
                                    value={form.firstName}
                                    onChange={(event) => setForm({ ...form, firstName: event.target.value })}
                                    className="mt-2 w-full rounded-3xl border border-green-400 bg-green-50 px-4 py-3 text-green-900 focus:border-green-500 focus:outline-none"
                                    placeholder="Prénom"
                                />
                            </div>
                        </div>

                        {/* Row 2: Nom, Fonction */}
                        <div className="grid gap-4 sm:grid-cols-2">
                            <div>
                                <label className="text-sm text-green-600">Nom</label>
                                <input
                                    value={form.lastName}
                                    onChange={(event) => setForm({ ...form, lastName: event.target.value })}
                                    className="mt-2 w-full rounded-3xl border border-green-400 bg-green-50 px-4 py-3 text-green-900 focus:border-green-500 focus:outline-none"
                                    placeholder="Nom"
                                />
                            </div>
                            <div>
                                <label className="text-sm text-green-600">Fonction</label>
                                <input
                                    value={form.position}
                                    onChange={(event) => setForm({ ...form, position: event.target.value })}
                                    className="mt-2 w-full rounded-3xl border border-green-400 bg-green-50 px-4 py-3 text-green-900 focus:border-green-500 focus:outline-none"
                                    placeholder="Ex. Responsable achats"
                                />
                            </div>
                        </div>

                        {/* Row 3: Adresse, Ville */}
                        <div className="grid gap-4 sm:grid-cols-2">
                            <div>
                                <label className="text-sm text-green-600">Adresse</label>
                                <input
                                    value={form.address}
                                    onChange={(event) => setForm({ ...form, address: event.target.value })}
                                    className="mt-2 w-full rounded-3xl border border-green-400 bg-green-50 px-4 py-3 text-green-900 focus:border-green-500 focus:outline-none"
                                    placeholder="Adresse"
                                />
                            </div>
                            <div>
                                <label className="text-sm text-green-600">Ville</label>
                                <input
                                    value={form.city}
                                    onChange={(event) => setForm({ ...form, city: event.target.value })}
                                    className="mt-2 w-full rounded-3xl border border-green-400 bg-green-50 px-4 py-3 text-green-900 focus:border-green-500 focus:outline-none"
                                    placeholder="Ville"
                                />
                            </div>
                        </div>

                        {/* Row 4: NIF, STAT */}
                        <div className="grid gap-4 sm:grid-cols-2">
                            <div>
                                <label className="text-sm text-green-600">NIF</label>
                                <input
                                    value={form.nif}
                                    onChange={(event) => setForm({ ...form, nif: event.target.value })}
                                    className="mt-2 w-full rounded-3xl border border-green-400 bg-green-50 px-4 py-3 text-green-900 focus:border-green-500 focus:outline-none"
                                    placeholder="Numéro d'identification"
                                />
                            </div>
                            <div>
                                <label className="text-sm text-green-600">STAT</label>
                                <input
                                    value={form.stat}
                                    onChange={(event) => setForm({ ...form, stat: event.target.value })}
                                    className="mt-2 w-full rounded-3xl border border-green-400 bg-green-50 px-4 py-3 text-green-900 focus:border-green-500 focus:outline-none"
                                    placeholder="Numéro STAT"
                                />
                            </div>
                        </div>

                        {/* Row 5: Email, Téléphone */}
                        <div className="grid gap-4 sm:grid-cols-2">
                            <div>
                                <label className="text-sm text-green-600">Adresse email</label>
                                <input
                                    type="email"
                                    value={form.email}
                                    onChange={(event) => setForm({ ...form, email: event.target.value })}
                                    className="mt-2 w-full rounded-3xl border border-green-400 bg-green-50 px-4 py-3 text-green-900 focus:border-green-500 focus:outline-none"
                                    placeholder="email@example.com"
                                />
                            </div>
                            <div>
                                <label className="text-sm text-green-600">N° téléphone</label>
                                <input
                                    value={form.phone}
                                    onChange={(event) => setForm({ ...form, phone: event.target.value })}
                                    className="mt-2 w-full rounded-3xl border border-green-400 bg-green-50 px-4 py-3 text-green-900 focus:border-green-500 focus:outline-none"
                                    placeholder="+33 6 12 34 56 78"
                                />
                            </div>
                        </div>

                        {/* Row 6: Autres contacts */}
                        <div className="grid gap-4 sm:grid-cols-2">
                            <div>
                                <label className="text-sm text-green-600">Autre contact</label>
                                <input
                                    value={form.otherContact}
                                    onChange={(event) => setForm({ ...form, otherContact: event.target.value })}
                                    className="mt-2 w-full rounded-3xl border border-green-400 bg-green-50 px-4 py-3 text-green-900 focus:border-green-500 focus:outline-none"
                                    placeholder="Autre contact"
                                />
                            </div>
                            <div>
                                <label className="text-sm text-green-600">En contact avec</label>
                                <input
                                    value={form.contactPerson}
                                    onChange={(event) => setForm({ ...form, contactPerson: event.target.value })}
                                    className="mt-2 w-full rounded-3xl border border-green-400 bg-green-50 px-4 py-3 text-green-900 focus:border-green-500 focus:outline-none"
                                    placeholder="Personne ressource"
                                />
                            </div>
                        </div>

                        {/* Commentaire */}
                        <div>
                            <label className="text-sm text-green-600">Commentaire</label>
                            <textarea
                                value={form.comment}
                                onChange={(event) => setForm({ ...form, comment: event.target.value })}
                                className="mt-2 w-full rounded-3xl border border-green-400 bg-green-50 px-4 py-3 text-green-900 focus:border-green-500 focus:outline-none"
                                placeholder="Commentaire"
                                rows={3}
                            />
                        </div>

                        {/* Articles achetés chez ce fournisseur */}
                        {selectedId && (
                            <div className="rounded-2xl border border-green-200 bg-green-50/50 p-4">
                                <h4 className="mb-3 font-semibold text-green-900">Articles achetés chez ce fournisseur</h4>
                                {loadingItems ? (
                                    <p className="text-sm text-green-600">Chargement...</p>
                                ) : purchasedItems.length > 0 ? (
                                    <div className="max-h-40 overflow-y-auto space-y-2">
                                        {purchasedItems.map((item, idx) => (
                                            <div key={idx} className="rounded border border-green-200 bg-white p-2 text-sm">
                                                <p className="font-medium text-green-800">{item.item_name}</p>
                                                <p className="text-xs text-green-600">
                                                    {item.quantity} {item.unit} — {item.purchase_price} Ar
                                                </p>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-sm text-green-600">Aucun article trouvé</p>
                                )}
                            </div>
                        )}

                        {/* Buttons */}
                        <div className="flex gap-3">
                            <button
                                type="submit"
                                className="rounded-3xl bg-green-600 px-5 py-3 font-semibold text-slate-950 hover:bg-green-500"
                            >
                                Enregistrer
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    setSelectedId(null);
                                    setForm({
                                        companyName: '',
                                        firstName: '',
                                        lastName: '',
                                        position: '',
                                        email: '',
                                        phone: '',
                                        otherContact: '',
                                        contactPerson: '',
                                        address: '',
                                        city: '',
                                        nif: '',
                                        stat: '',
                                        comment: '',
                                    });
                                }}
                                className="rounded-3xl border border-green-300 bg-green-50 px-5 py-3 text-green-800 hover:border-green-500"
                            >
                                Annuler
                            </button>
                        </div>
                    </form>
                </div>

                {/* Liste en second - scrollable */}
                <div className="rounded-3xl border border-green-400 bg-white p-5 shadow-xl shadow-slate-950/20">
                    <h3 className="mb-4 text-lg font-semibold text-green-900">
                        Fournisseurs enregistrés ({suppliers.length})
                    </h3>
                    <div className="max-h-96 overflow-y-auto space-y-3">
                        {suppliers.length === 0 ? (
                            <div className="rounded-2xl border border-green-200 bg-green-50 p-4 text-center text-green-600">
                                Aucun fournisseur enregistré pour le moment.
                            </div>
                        ) : (
                            suppliers.map((supplier) => (
                                <div
                                    key={supplier.id}
                                    className="rounded-2xl border border-green-200 bg-green-50/50 p-4"
                                >
                                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                                        <div className="flex-1">
                                            <p className="font-semibold text-green-900">{supplier.companyName || supplier.company_name}</p>
                                            <p className="text-sm text-green-700">
                                                {supplier.lastName || supplier.last_name ? `${supplier.lastName || supplier.last_name || ''} ${supplier.firstName || supplier.first_name || ''}`.trim() : 'Contact non défini'}
                                            </p>
                                            <div className="mt-2 grid gap-2 text-xs text-green-600">
                                                {supplier.nif && <p>NIF: {supplier.nif}</p>}
                                                {supplier.stat && <p>STAT: {supplier.stat}</p>}
                                                {(supplier.address || supplier.city) && <p>📍 {supplier.address || ''} {supplier.city || ''}</p>}
                                                {supplier.email && <p>✉️ {supplier.email}</p>}
                                                {supplier.phone && <p>📞 {supplier.phone}</p>}
                                            </div>
                                        </div>
                                        <div className="flex gap-2">
                                            <button
                                                onClick={() => setSelectedId(supplier.id)}
                                                className="rounded-2xl border border-green-300 bg-green-50 px-3 py-2 text-xs font-medium text-green-800 hover:border-green-500 whitespace-nowrap"
                                            >
                                                Éditer
                                            </button>
                                            <button
                                                onClick={() => onDeleteSupplier(supplier.id)}
                                                className="rounded-2xl border border-rose-300 bg-red-50 px-3 py-2 text-xs font-medium text-rose-700 hover:bg-rose-100 whitespace-nowrap"
                                            >
                                                Supprimer
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
